<template>
  <div>
    <div v-if="editingReceiving && isTypeBulk" class="order-item-toolbar row mb-1">
      <div class="col-12">
        <span class="font-weight-bold mr-3">Tools</span>
        <FormButton class="btn-default btn-xs mr-2" @click="displayAddItems">
          <i class="far fa-plus"></i> Add Items
        </FormButton>
      </div>

      <RecvItemAddItemsPopover ref="addItemsPopover" :stockLookupList="stockLookupList" @addItems="addBulkRecvItems" />
    </div>

    <StaticDataTable class="static-table-list order-item-list">
      <template v-slot:header>
        <tr>
          <th width="1%">#</th>
          <th :width="editingReceiving ? '17%' : '13%'">Stock</th>
          <th width="8%" v-if="!editingReceiving && isTypeBulk">Barcode</th>
          <th width="8%" v-if="isTypeStandard || isTypeExternalOrder">Block</th>
          <th width="8%">Ordered Size (HxW)</th>
          <th width="7%">Height</th>
          <th width="7%">Width</th>
          <th width="7%">Qty / Sheets</th>
          <th width="10%">Batch</th>
          <th width="8%">Units</th>
          <th width="7%">Unit Price</th>
          <th width="5%">Price</th>                        
          <th width="5%">%</th>
          <th width="5%">Add. Costs</th>
          <th width="6%">Line Price</th>
          <th width="5%">Actions</th>
        </tr>
      </template>
      <template v-slot:body>
        <template v-for="(recvItem, index) in recvItemList">

          <tr v-if="index > 0" class="order-item-spacer" :key="`item-${index}-spacer`">
            <td :colspan="(editingReceiving && isTypeBulk) ? 14 : 14"> </td>
          </tr>
          <tr
            :key="recvItem.id" 
            :class="[
              'order-item-row', `order-item-row-group-${recvItem.stockType}`,
              { 'goods-received-zeroed': (isRecvItemCleared(recvItem) && !recvItem.isAdjustment),
              'goods-received-amendment': recvItem.isAmendment,
              'goods-received-static': !editingReceiving }]">
            <td>
              {{index + 1}}
            </td>
            <td>
              <template v-if="!recvItem.isAdjustment">
                <LinkStock :id="recvItem.stock.id" :name="`${recvItem.stock.code} - ${recvItem.stock.name}`" />
                <span v-if="isRecvItemGlass(recvItem)"><br>{{recvItemDescription(recvItem)}}</span>
              </template>
              <FormSearchableSelect v-if="recvItem.isAdjustment && editingReceiving" :options="stockLookupList" :value="recvItem.stockId" @input="recvItemSetItemStockId(recvItem, $event)" placeholder="Stock Code" dropdownAutoWidth />
            </td>
            <td v-if="!editingReceiving && isTypeBulk">
              {{recvItem.barcode ? recvItem.barcode : '-'}}
            </td>
            <td v-if="isTypeStandard && isTypeExternalOrder">
              {{(recvItem.purchaseOrderItem && recvItem.purchaseOrderItem.stockPriceGroup) ? recvItem.purchaseOrderItem.stockPriceGroup.name : '-'}}
            </td>
            <td v-if="isTypeStandard || isTypeExternalOrder">
              <template v-if="isRecvItemGlass(recvItem) && !isRecvItemGlassProcessed(recvItem)">
                <IconBoolean v-if="!editingReceiving" :value="recvItem.isBlock" :displayFalse="false" />
                <FormCheckbox v-show="editingReceiving" v-model="recvItem.isBlock" @change="itemListUpdated()" />
              </template>
              <template v-else>
                -
              </template>
            </td>
            <td>
              <template v-if="!isTypeExternalOrder && isRecvItemGlass(recvItem)">
                <span v-if="(!editingReceiving || !recvItem.isAdjustment) && recvItem.purchaseOrderItem">{{recvItem.purchaseOrderItem.glassHeight}} x {{recvItem.purchaseOrderItem.glassWidth}}</span>
                <span v-if="(!editingReceiving || !recvItem.isAdjustment) && !recvItem.purchaseOrderItem && recvItem.glassSize">{{recvItem.glassSize.height}} x {{recvItem.glassSize.width}}</span>
                <FormSelect v-if="editingReceiving && recvItem.isAdjustment && !recvItem.loading" :options="recvItem.glassSizeOptions" :value="recvItem.glassSizeId" @input="recvItemGlassSizeUpdated(recvItem, $event)" />
              </template>
              <template v-if="isTypeExternalOrder && isRecvItemGlass(recvItem)">
                <span v-if="(!editingReceiving || !recvItem.isAdjustment) && recvItem.purchaseOrderItem">{{recvItem.purchaseOrderItem.glassHeight}} x {{recvItem.purchaseOrderItem.glassWidth}}</span>
              </template>
              <template v-if="!isRecvItemGlass(recvItem)">
                -
              </template>
              <Spinner v-if="editingReceiving && recvItem.isAdjustment && recvItem.loading" />
            </td>
            <td>
              <template v-if="isRecvItemGlass(recvItem)">
                <FormInputText type="number" v-model="recvItem.glassHeight" :width="6" v-if="editingReceiving" @input="itemListUpdated()" />
                <span v-if="!editingReceiving">{{recvItem.glassHeight}}</span>
              </template>
              <template v-else>
                -
              </template>
            </td>
            <td>
              <template v-if="isRecvItemGlass(recvItem)">
                <FormInputText type="number" v-model="recvItem.glassWidth" :width="6" v-if="editingReceiving" @input="itemListUpdated()" />
                <span v-if="!editingReceiving">{{recvItem.glassWidth}}</span>
              </template>
              <template v-else>
                -
              </template>
            </td>
            <td>
              <template v-if="isRecvItemGlass(recvItem) && recvItem.isBlock">
                <FormInputText type="number" v-model="recvItem.blockSize" :width="4" v-if="editingReceiving" @input="itemListUpdated()" />
                <span v-if="!editingReceiving">{{recvItem.blockSize}}</span>
                <small class="purchase-order-item-receiving-description">Sheets in block</small>
              </template>
              <template v-if="!isRecvItemGlass(recvItem) || !recvItem.isBlock">
                <FormInputText type="number" v-model="recvItem.quantity" :width="4" v-if="editingReceiving" @input="itemListUpdated()" />
                <span v-if="!editingReceiving">{{recvItem.quantity}}</span>
              </template>
            </td>
            <td>
              <FormInputText v-model="recvItem.supplierBatchRef" :width="8" v-if="editingReceiving" @input="itemListUpdated()" />
              <span v-if="!editingReceiving">{{recvItem.supplierBatchRef}}</span>
            </td>
            <td>
              <UOMValue v-if="(!editingReceiving || isRecvItemGlass(recvItem)) && recvItem.stock" :value="calculateRecvItemUnits(recvItem)" :uomType="recvItem.stock.uomType" />
              <FormInputText type="number" v-model="recvItem.units" :width="4" v-if="editingReceiving && !isRecvItemGlass(recvItem)" @input="itemListUpdated()" />
            </td>
            <td>
              <FormInputText v-if="editingReceiving" type="number" v-model="recvItem.unitPrice" :width="4" @input="itemListUpdated()" />
              <span v-if="!editingReceiving">{{recvItem.unitPrice | formatDecimal}}</span>
            </td>
            <td>{{calculateRecvItemPrePrice(recvItem) | formatDecimal}}</td>
            <td>{{calculateRecvItemPrePricePercent(recvItem) | formatPercent}}</td>
            <td>{{calculateRecvItemAddCost(recvItem) | formatDecimal}}</td>
            <td>
              <span class="font-weight-bold">{{calculateRecvItemLinePrice(recvItem) | formatDecimal}}</span>
              <Tooltip label="Adjusted Unit Price" v-show="calculateRecvItemAddUnitPrice(recvItem) > 0">&nbsp;(<span class="pricing-list-variation">{{calculateRecvItemAddUnitPrice(recvItem) | formatDecimal}}</span>)</Tooltip>
            </td>
            <td>
              <ActionDropMenu :tableAction="true" :forceDropdown="true">
                <DropdownMenuItem @click="displayReceivingLabels(recvItem.id)" icon="fa-barcode-scan" v-if="recvItem.isBlock && recvItem.barcode && !editingReceiving && isOrderComplete">Display Label</DropdownMenuItem>
                <DropdownMenuItem v-if="isOrderComplete && isTypeExternalOrder && recvItem.quantity > 0 && recvItem.productionPanelId" @click="displayProductionPanelLabels(recvItem.id)" icon="fa-print">Production Panel Label</DropdownMenuItem>
                <DropdownMenuItem @click="recvItemClearQuantity(recvItem)" icon="fa-times-square" v-if="editingReceiving && !recvItem.isAdjustment">Goods Not Received</DropdownMenuItem>
                <DropdownMenuItem @click="recvRemoveNewLineItem(index)" icon="fa-trash-alt" v-if="editingReceiving && recvItem.isAdjustment">Remove Item</DropdownMenuItem>
              </ActionDropMenu>
            </td>
          </tr>
        </template>
        <tr class="static-datatable-row-nostripe border-top">
          <td colspan="2">
            <a class="btn btn-white btn-sm" @click.prevent="recvAddNewLineItem" v-if="editingReceiving && !isTypeExternalOrder"><i class="far fa-plus"></i> Add Item</a>
          </td>
          <td v-if="!editingReceiving && isTypeBulk"> </td>
          <td colspan="5" class="font-weight-bold text-right">Totals:</td>
          <td class="font-weight-bold">
            <span v-if="isTypeBulk">{{calculateRecvTotalUnits()}}</span>
          </td>
          <td> </td>
          <td class="font-weight-bold">{{calculateRecvTotalPrePrice() | formatCurrency}}</td>
          <td> </td>
          <td class="font-weight-bold">{{calculateRecvTotalItemsAdditionalCosts() | formatCurrency}}</td>
          <td class="font-weight-bold">{{calculateRecvTotalLinePrice() | formatCurrency}}</td>
          <td> </td>
        </tr>
      </template>
    </StaticDataTable>
  </div>
</template>

<style scoped>
.goods-received-zeroed {
  text-decoration: line-through;
}
.goods-received-zeroed.goods-received-static {
  color: #A00000 !important;
}
.goods-received-amendment.goods-received-static {
  color: #009000 !important;
}
</style>

<script>
import _ from 'lodash'

import Utils from '@/utils/utils'
import StaticDataTable from '@/components/table/staticDataTable'

import RecvItemAddItemsPopover from '@/modules/purchasing/components/recvItemAddItemsPopover'

import { LinkTypes, PriceTypes, StockPriceTypes, ApprovalTypes, PricingRequestStatusTypes, InventoryStockTypes, InventoryStockTypeLookup } from '@/store/types'
import { PurchaseOrderTypes, PurchaseOrderTypeLookup, PurchaseOrderStatusTypes, PurchaseOrderStatusTypeLookup, DescriptorLookupTypes } from '@/store/types'

export default {
  name: 'ReceivingItemList',
  model: {
    event: 'update'
  },
  components: {
    StaticDataTable,
    RecvItemAddItemsPopover
  },
  props: {
    editingReceiving: Boolean,
    totalAdditionalCost: Number,
    order: Object,
    value: Array,
    stockLookupList: Array
  },
  data() {
    return {
      recvItemList: _.cloneDeep(this.$props.value || [])
    }
  },
  watch: {
    value: function(newVal) {
      this.$data.recvItemList = _.cloneDeep(this.$props.value || [])
    },
    totalAdditionalCost: function() {
      this.$nextTick(() => {
        this.itemListUpdated()
      })
    }
  },
  computed: {
    isTypeBulk() {
      return this.order.type == PurchaseOrderTypes.bulk
    },
    isTypeStandard() {
      return this.order.type == PurchaseOrderTypes.standard
    },
    isTypeExternalOrder() {
      return this.order.type == PurchaseOrderTypes.externalOrder
    },
    isOrderComplete() {
      return this.order.status == PurchaseOrderStatusTypes.completed
    },
  },
  methods: {
    itemListUpdated() {
      this.$emit('update', this.$data.recvItemList)
    },
    isRecvItemGlass(item) {
      return item.stockType == InventoryStockTypes.glass
    },
    recvItemDescription(item) {
      if(item.stockType == InventoryStockTypes.glass) {
        if(item.purchaseOrderItem && item.purchaseOrderItem.stockPriceGroup)
          return `Pricing: ${item.purchaseOrderItem.stockPriceGroup.name}`
      }
      return ''
    },
    isRecvItemGlassProcessed(item) {
      if(item.purchaseOrderItem && item.purchaseOrderItem.isProcessedItem)
        return true
      return false
    },
    calculateRecvItemUnits(item) {
      if(item.stockType == InventoryStockTypes.glass) {
        let areaMM = item.glassWidth * item.glassHeight
        return Utils.roundQuantityValue(Utils.convertMillimetreSqToMetreSq(areaMM))
      }
      return item.units
    },
    calculateRecvItemTotalUnits(item) {
      if(item.stockType == InventoryStockTypes.glass) {
        let areaMM = item.glassWidth * item.glassHeight
        // Note: Block forces to 1 quantity (eg, 1 block per received line item)
        if(item.isBlock)
          areaMM *= item.blockSize
        else
          areaMM *= item.quantity
        return Utils.roundQuantityValue(Utils.convertMillimetreSqToMetreSq(areaMM))
      }
      return item.units * item.quantity
    },
    calculateRecvTotalUnits() {
      if(this.recvItemList == null)
        return 0
      let totalUnits = this.recvItemList.reduce((curTotal, item) => {
        return curTotal + this.calculateRecvItemTotalUnits(item)
      }, 0)
      return Utils.roundQuantityValue(totalUnits)
    },
    // Line item price - Pre Additional Costs
    calculateRecvItemPrePrice(item) {
      return Utils.roundCurrency(this.calculateRecvItemTotalUnits(item) * Utils.roundCurrency(item.unitPrice))
    },
    calculateRecvTotalPrePrice() {
      if(this.recvItemList == null)
        return 0
      let totalPrice = this.recvItemList.reduce((curTotal, item) => {
        return curTotal + this.calculateRecvItemPrePrice(item)
      }, 0)
      return Utils.roundCurrency(totalPrice)
    },
    calculateRecvItemPrePricePercent(item) {
      let totalPrePrice = this.calculateRecvTotalPrePrice()
      let itemPrePrice = this.calculateRecvItemPrePrice(item)
      if(itemPrePrice > 0) {
        return (itemPrePrice / totalPrePrice) * 100.0
      }
      return 0
    },
    calculateRecvItemAddCost(item) {
      return Utils.roundCurrency(this.calculateRecvItemPrePricePercent(item) * 0.01 * this.totalAdditionalCost)
    },
    calculateRecvItemLinePrice(item) {
      return Utils.roundCurrency(this.calculateRecvItemPrePrice(item) + this.calculateRecvItemAddCost(item))
    },
    calculateRecvItemAddUnitPrice(item) {
      if(this.calculateRecvItemAddCost(item) == 0)
        return 0
      let totalUnits = this.calculateRecvItemTotalUnits(item)
      if(totalUnits <= 0)
        return 0
      return Utils.roundCurrency(this.calculateRecvItemLinePrice(item) / totalUnits)
    },
    // Calculate total additional costs based on items, including small accumulated cents
    // Actual additional costs may not always be the same if additional costs cannot be equally divided amongst the items
    calculateRecvTotalItemsAdditionalCosts() {
      if(this.recvItemList == null)
        return 0
      let totalPrice = this.recvItemList.reduce((curTotal, item) => {
        return curTotal + this.calculateRecvItemAddCost(item)
      }, 0)
      return Utils.roundCurrency(totalPrice)
    },
    calculateRecvTotalLinePrice() {
      if(this.recvItemList == null)
        return 0
      let totalPrice = this.recvItemList.reduce((curTotal, item) => {
        return curTotal + this.calculateRecvItemLinePrice(item)
      }, 0)
      return Utils.roundCurrency(totalPrice)
    },
    recvRemoveNewLineItem(index) {
      this.recvItemList.splice(index, 1)
      this.itemListUpdated()
    },
    recvItemClearQuantity(item) {
      if(item.isBlock)
        item.blockSize = 0
      else
        item.quantity = 0

      this.itemListUpdated()
    },
    isRecvItemCleared(item) {
      if(item.isBlock && item.blockSize == 0)
        return true
      return item.quantity == 0
    },

    recvItemSetItemStockId(item, stockId) {
      item.stockId = stockId
      if(item.stockId == null) {
        item.loading = false
        item.glassSizeList = []
        item.glassSizeOptions = []
        item.glassSizeId = null

        this.itemListUpdated()
        return
      }

      item.loading = true

      this.$apiService.inventoryService.getStock(item.stockId)
      .then(response => {
        item.stock = {
          id: response.data.id,
          name: response.data.name,
          code: response.data.code,
          uomType: response.data.uomType
        }
        // Get the glass sizing list for the stock
        item.glassSizeList = response.data.glassSizeList.filter(glassSize => glassSize.isActive)
        item.glassSizeOptions = item.glassSizeList.map(glassSize => {
          return { value: glassSize.id, name: `${glassSize.height} x ${glassSize.width}` }
        })
        if(item.glassSizeOptions.length > 0) {
          var glassSizeId = item.glassSizeOptions[0].value      // Default to first

          this.recvItemGlassSizeUpdated(item, glassSizeId)   
        } else {
          item.glassSizeId = null
        }
        item.loading = false
        this.itemListUpdated()
      })
      .catch(err => {
        item.loading = false
      })
    },
    recvItemGlassSizeUpdated(item, glassSizeId) {
      item.glassSizeId = glassSizeId

      // Set details
      item.stockContainerCode = ''
      item.blockSize = null
      item.unitPrice = 0

      var glassSize = item.glassSizeList.find(glassSize => glassSize.id == glassSizeId)
      if(glassSize) {
        if(glassSize.stockContainer) {
          item.stockContainerCode = glassSize.stockContainer.code
        }
        item.blockSize = parseInt(glassSize.blockSize)
        item.glassHeight = parseInt(glassSize.height)
        item.glassWidth = parseInt(glassSize.width)

        // Calculate pricing for supplier / stock / container combination
        // Do we need to deal with this? YES if item is an adjustment
        /*if(glassSize.stockContainer) {
          var stockPrice = this.supplierBulkPricingData.stockBulkPriceList.find(stock => stock.id == item.stockId)
          if(stockPrice) {
            var priceInfo = stockPrice.prices.find(containerPrice => containerPrice.stockContainerId == glassSize.stockContainer.id)
            if(priceInfo)
              item.unitPrice = priceInfo.price
          }
        }*/
      }

      this.itemListUpdated()
    },
    recvAddNewLineItem() {
      // Note: Not available for order linked PO's
      if(this.isTypeExternalOrder)
        return

      this.recvItemList.push({
        id: null,
        isAmendment: true,
        isAdjustment: true,
        unitPrice: 0,
        stockType: InventoryStockTypes.glass,
        isBlock: this.isTypeBulk,
        supplierBatchRef: '',
        quantity: 1,
        units: 1,
        blockSize: 0,
        glassHeight: 0,
        glassWidth: 0,
       stockId: null,
       glassSizeId: null,
        glassSize: {}
      })

      this.itemListUpdated()
    },

    displayReceivingLabels(recvItemId) {
      this.$emit('displayReceivingLabels', recvItemId)
    },
    displayProductionPanelLabels(recvItemId) {
      this.$emit('displayProductionPanelLabels', recvItemId)
    },

    displayAddItems(ev) {
      this.$refs.addItemsPopover.displayFromElement(ev.target)
    },
    addBulkRecvItems(info) {
      // Only bulk supported
      if(!this.isTypeBulk)
        return

      for(let i=0; i<info.quantity; i++) {
        this.recvItemList.push({
          id: null,
          loading: false,
          isAmendment: true,
          isAdjustment: true,
          unitPrice: 0,
          stockType: InventoryStockTypes.glass,
          isBlock: this.isTypeBulk,
          supplierBatchRef: '',
          quantity: 1,
          units: 1,
          blockSize: info.blockSize,
          glassHeight: info.glassSize.height,
          glassWidth: info.glassSize.width,
          stockId: info.stockId,
          stock: info.stock,
          glassSizeId: info.glassSizeId,
          glassSize: info.glassSize,
          glassSizeList: info.glassSizeList,
          glassSizeOptions: info.glassSizeList.map(glassSize => {
            return { value: glassSize.id, name: `${glassSize.height} x ${glassSize.width}` }
          }),
          stockContainerCode: info.glassSize.stockContainer ? info.glassSize.stockContainer.code : null
        })
      }

      this.itemListUpdated()
    }

  }
}
</script>